import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { autorun } from 'mobx';
import * as Sentry from '@sentry/browser';

import { UNREAD_THREAD_SEATBELT, UNSEEN_THREAD_REPLY_LIMIT } from '../Model/ChatChannel';
import ChatThreadSeen from '../Model/ChatThreadSeen';
import {
  getThreadFirstSight,
  groupThreadRows,
  replyTime,
} from '../utils/threadSeen';

/**
 * The coach's unread thread inbox for the open channel: which threads carry replies they have not
 * read, which threads the channel saw recently, and the writer that marks one read.
 *
 * Read marks live in Firestore rather than on the device, so marking a thread read on mobile empties
 * the same row here. The document is subscribed, not fetched, which is what makes that immediate.
 * @param {Object} activeChannel Channel document, or null while none is open
 * @param {String} currentUserId Viewer's user id — their own replies are never announced
 * @returns {Object} Inbox rows, the unread count, and the read-mark writer
 */
const useUnseenThreads = (activeChannel, currentUserId) => {
  const [recentThreadReplies, setRecentThreadReplies] = useState([]);
  const [unreadThreadReplies, setUnreadThreadReplies] = useState([]);
  const [threadSeenDoc, setThreadSeenDoc] = useState(null);
  const [threadSeenByRoot, setThreadSeenByRoot] = useState({});
  const [threadSeenBefore, setThreadSeenBefore] = useState(undefined);
  const [isThreadInboxOpen, setIsThreadInboxOpen] = useState(false);
  const [threadNoticeRootId, setThreadNoticeRootId] = useState(null);
  // What this coach's marks looked like when the channel first opened. `wasEmpty` marks a channel
  // never opened by them anywhere; `watermark` is the newest reply at that moment, which separates
  // history to absorb from a reply that genuinely arrived while the coach was looking; `seeded`
  // records that the one baseline write for this channel has already been attempted.
  const firstSightRef = useRef({
    channelId: '', wasEmpty: false, watermark: 0, seeded: false,
  });

  /*
    Watches this channel's most recent thread replies through their own query, not a filter over the
    loaded message window: that window is capped by pagination, so a reply buried under newer traffic
    could never be announced — we would not even hold its id.
  */
  useEffect(() => {
    setRecentThreadReplies([]);
    // Close a pill-opened thread and the inbox on channel switch — both belong to the channel being left.
    setThreadNoticeRootId(null);
    setIsThreadInboxOpen(false);
    if (!activeChannel) {
      return undefined;
    }

    let cancelled = false;
    let disposer;
    activeChannel.getRecentThreadReplies({ limit: UNSEEN_THREAD_REPLY_LIMIT })
      .then((collection) => {
        if (!cancelled) {
          disposer = autorun(() => setRecentThreadReplies([...collection.docs]));
        }
      })
      .catch((error) => {
        // A missing composite index is the likely failure and cannot be fixed at runtime; the notice
        // stays hidden rather than taking the channel down.
        Sentry.captureException(error, {
          extra: {
            errorMessage: 'Failed to load recent thread replies',
            channelId: activeChannel.id,
          },
        });
      });

    return () => {
      cancelled = true;
      if (disposer) {
        disposer();
      }
    };
  }, [activeChannel]);

  /*
    The unread query waits for the marks document: running it before the watermark loads would bound
    it at 0 — every reply the channel ever had — and then re-subscribe anyway.
  */
  useEffect(() => {
    setUnreadThreadReplies([]);
    if (!activeChannel || !threadSeenDoc) {
      return undefined;
    }

    let cancelled = false;
    let disposer;
    activeChannel.getUnreadThreadReplies({
      since: threadSeenBefore ?? 0,
      limit: UNREAD_THREAD_SEATBELT,
    })
      .then((collection) => {
        if (!cancelled) {
          disposer = autorun(() => setUnreadThreadReplies([...collection.docs]));
        }
      })
      .catch((error) => {
        Sentry.captureException(error, {
          extra: {
            errorMessage: 'Failed to load unread thread replies',
            channelId: activeChannel.id,
          },
        });
      });

    return () => {
      cancelled = true;
      if (disposer) {
        disposer();
      }
    };
  }, [activeChannel, threadSeenDoc, threadSeenBefore]);

  /*
    The marks document is observed rather than read once, so a thread marked read on another device
    empties its row here without a refresh. A coach who has never opened this channel has no
    document at all, which reads as an empty map — the same thing the baseline effect below expects.
  */
  useEffect(() => {
    setThreadSeenByRoot({});
    setThreadSeenBefore(undefined);
    setThreadSeenDoc(null);
    if (!activeChannel || !currentUserId) {
      return undefined;
    }

    let cancelled = false;
    let disposer;
    const doc = new ChatThreadSeen(currentUserId, activeChannel.id);
    doc.init()
      .then(() => {
        if (cancelled) {
          return;
        }
        /*
          Published only once it has loaded, and subscribed before it is published — never the other
          way round. An unloaded document reads as an empty map, indistinguishable from a coach who
          has never opened this channel, and the seeding effect below takes that as licence to absorb
          everything. React 17 does not batch a promise continuation, so publishing first commits a
          render where the map is still empty and that effect can run in the gap.
        */
        disposer = autorun(() => {
          setThreadSeenByRoot(doc.seenByRoot);
          setThreadSeenBefore(doc.seenBefore);
        });
        setThreadSeenDoc(doc);
      })
      .catch((error) => {
        // Without the marks the inbox announces history the coach has already read; noisy, not broken.
        Sentry.captureException(error, {
          extra: {
            errorMessage: 'Failed to load thread read marks',
            channelId: activeChannel.id,
          },
        });
      });

    return () => {
      cancelled = true;
      if (disposer) {
        disposer();
      }
    };
  }, [activeChannel, currentUserId]);

  const markThreadsRead = useCallback((marks) => {
    if (!threadSeenDoc) {
      /*
        Nowhere to write the mark yet. The inbox and the thread modal both open from inside a channel
        whose marks have loaded, so this should be unreachable — which is why it is reported rather
        than queued the way mobile has to. If that precondition ever stops holding, the answered
        thread stays in the inbox and this is the only thing that says why.
      */
      Sentry.captureMessage('Dropped a thread read mark before the marks document loaded', {
        extra: {
          channelId: activeChannel ? activeChannel.id : null,
          rootMessageIds: Object.keys(marks).join(','),
        },
      });
      return;
    }
    threadSeenDoc.markThreadsRead(marks).catch((error) => {
      Sentry.captureException(error, {
        extra: {
          errorMessage: 'Failed to mark threads read',
          channelId: threadSeenDoc.channelId,
          rootMessageIds: Object.keys(marks).join(','),
        },
      });
    });
  }, [threadSeenDoc, activeChannel]);

  const markThreadRead = useCallback(
    (rootId, newestReplyAt) => {
      if (!rootId || !newestReplyAt) {
        return;
      }
      markThreadsRead({ [rootId]: newestReplyAt });
    },
    [markThreadsRead],
  );

  /*
    A channel this coach has never opened has no read marks, so every historical reply would read as
    new — a coach migrated from Slack has a whole backfilled history of them. Absorb what the query
    returned as already read, and leave anything newer to be announced.
  */
  useEffect(() => {
    if (!activeChannel || !threadSeenDoc || recentThreadReplies.length === 0) {
      return;
    }
    /*
      Read from the document, never from state: the state copy can lag it by a render, and a map
      that lags is indistinguishable from a coach with no marks at all — which is licence to absorb
      their entire unread queue. That is also why the marks are not in this effect's dependencies:
      reacting to them is what made the write → echo → write loop possible.
    */
    if (firstSightRef.current.channelId !== activeChannel.id) {
      firstSightRef.current = {
        channelId: activeChannel.id,
        ...getThreadFirstSight(threadSeenDoc.seenByRoot, threadSeenDoc.seenBefore, recentThreadReplies),
        seeded: false,
      };
    }
    const { wasEmpty, watermark, seeded } = firstSightRef.current;
    if (!wasEmpty || seeded) {
      return;
    }
    // Recorded before the write goes out: one shot per channel, not a reaction to its own echo.
    firstSightRef.current.seeded = true;
    threadSeenDoc.markSeenBefore(watermark).catch((error) => {
      Sentry.captureException(error, {
        extra: {
          errorMessage: 'Failed to write the thread read watermark',
          channelId: activeChannel.id,
        },
      });
    });
  }, [activeChannel, threadSeenDoc, recentThreadReplies]);

  const unreadThreadRows = useMemo(
    () => groupThreadRows({
      replies: unreadThreadReplies,
      seenByRoot: threadSeenByRoot,
      seenBefore: threadSeenBefore,
      currentUserId,
    }).filter(({ unreadCount }) => unreadCount > 0),
    [unreadThreadReplies, threadSeenByRoot, threadSeenBefore, currentUserId],
  );

  const recentThreadRows = useMemo(() => groupThreadRows({
    replies: recentThreadReplies,
    seenByRoot: threadSeenByRoot,
    seenBefore: threadSeenBefore,
    currentUserId,
  }), [recentThreadReplies, threadSeenByRoot, threadSeenBefore, currentUserId]);

  /*
    Clears the whole queue in one act — the only thing besides first sight that moves the watermark.
    Never an empty screen, which is also what an in-flight or failed query looks like. The newest
    reply comes from the Recent window: ordered newest-first and unfiltered by the watermark, so it
    is the highest timestamp on hand.
  */
  const markAllRead = useCallback(() => {
    // No value guard — `ChatThreadSeen.markSeenBefore` owns the "never zero, never backwards"
    // invariant, and one place holding it is what keeps both call sites honest.
    if (!threadSeenDoc) {
      return;
    }
    threadSeenDoc.markSeenBefore(Math.max(0, ...recentThreadReplies.map(replyTime))).catch((error) => {
      Sentry.captureException(error, {
        extra: {
          errorMessage: 'Failed to mark all threads read',
          channelId: threadSeenDoc.channelId,
        },
      });
    });
  }, [recentThreadReplies, threadSeenDoc]);

  return {
    // Threads, not replies: marking one thread read clears one row, so this is what counts down.
    unseenThreadCount: unreadThreadRows.length,
    unreadThreadRows,
    recentThreadRows,
    isUnreadTruncated: unreadThreadReplies.length >= UNREAD_THREAD_SEATBELT,
    markThreadRead,
    markAllRead,
    isThreadInboxOpen,
    setIsThreadInboxOpen,
    threadNoticeRootId,
    setThreadNoticeRootId,
  };
};

export default useUnseenThreads;
